import {
  AlignLeft,
  AudioLines,
  Boxes,
  BrainCircuit,
  ChevronDown,
  ChevronRight,
  ListOrdered,
  RotateCcw,
  Tag,
  TriangleAlert,
  Users,
  type LucideIcon,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useApp } from "@/lib/AppContext";
import {
  ApiError,
  type EngineDefinition,
  type EngineParameter,
  type EnginePlacement,
  type EngineSettings as EngineSettingsPayload,
  type ResolvedEngine,
  type ResolvedStep,
  type WorkerInventory,
} from "@/lib/api";
import { cn } from "@/lib/cn";

/**
 * UI-14 — Settings → Engines & models.
 *
 * The interface over ARCH-4, and the reason changing a model is a settings edit rather
 * than a redeploy. Everything drawn here comes from the Server's catalogue: the
 * placements, the models, the parameters and — the part that matters most on this
 * screen — whether anything implements a placement yet.
 *
 * Four of the seven engines have no implementation anywhere, and two of the three that
 * do have exactly one placement. Rather than offer a choice that would leave a step
 * nothing can pick up, an unavailable placement is drawn, disabled, with the milestone
 * that brings it — which is what every unfinished room in this app says.
 * The Server refuses the same choice, so the picker and the check cannot drift.
 */

const ICONS: Record<string, LucideIcon> = {
  Transcriber: AudioLines,
  Aligner: AlignLeft,
  Diarizer: Users,
  EntityTagger: Tag,
  Reasoner: BrainCircuit,
  Embedder: Boxes,
  Reranker: ListOrdered,
};

/** The handoff's colour per placement: local green, server blue, cloud pink. */
const PLACEMENT_COLOUR: Record<string, string> = {
  worker: "var(--vl-life)",
  server: "var(--vl-bridge)",
  cloud: "var(--vl-accent-warm)",
};

type ModelState = "present" | "fetching" | "failed" | "missing" | "no-worker";

export function EngineSettings({ summary }: { summary: string }) {
  const { api } = useApp();

  const [data, setData] = useState<EngineSettingsPayload | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await api.getEngines());
    } catch (cause: unknown) {
      setProblem(cause instanceof ApiError ? cause.message : "The engines could not be loaded.");
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  const chosen = useMemo(
    () => new Map((data?.engines ?? []).map((engine) => [engine.engine, engine])),
    [data],
  );

  async function save(
    engine: string,
    body: { placement: string; model?: string | null; parameters?: Record<string, unknown> },
  ) {
    if (busy) return;

    setBusy(true);
    setProblem(null);

    try {
      const stored = await api.setEngine(engine, body);

      // Patched in place rather than reloaded: the worker inventory has not changed,
      // and refetching would make every row flicker for one row's sake.
      setData((current) =>
        current === null
          ? current
          : {
              ...current,
              engines: current.engines.map((e) => (e.engine === engine ? stored : e)),
            },
      );
    } catch (cause: unknown) {
      setProblem(cause instanceof ApiError ? cause.message : "That could not be saved.");
    } finally {
      setBusy(false);
    }
  }

  /**
   * ARCH-12 — one step's parameters. The whole engine comes back, so the patch below is
   * the same one a placement change makes: the Server resolved the step inside its
   * engine, and re-deriving that here would be a second copy of the arithmetic.
   */
  async function saveStep(engine: string, step: string, parameters: Record<string, unknown>) {
    if (busy) return;

    setBusy(true);
    setProblem(null);

    try {
      const stored = await api.setEngineStep(engine, step, parameters);
      setData((current) =>
        current === null
          ? current
          : { ...current, engines: current.engines.map((e) => (e.engine === engine ? stored : e)) },
      );
    } catch (cause: unknown) {
      setProblem(cause instanceof ApiError ? cause.message : "That step could not be saved.");
    } finally {
      setBusy(false);
    }
  }

  async function resetStep(engine: string, step: string) {
    if (busy) return;

    setBusy(true);
    setProblem(null);

    try {
      const stored = await api.resetEngineStep(engine, step);
      setData((current) =>
        current === null
          ? current
          : { ...current, engines: current.engines.map((e) => (e.engine === engine ? stored : e)) },
      );
    } catch (cause: unknown) {
      setProblem(cause instanceof ApiError ? cause.message : "That step could not be reset.");
    } finally {
      setBusy(false);
    }
  }

  async function applyPreset(preset: string) {
    if (busy) return;

    setBusy(true);
    setProblem(null);

    try {
      const engines = await api.applyEnginePreset(preset);
      setData((current) => (current === null ? current : { ...current, engines }));
    } catch (cause: unknown) {
      setProblem(cause instanceof ApiError ? cause.message : "That preset could not be applied.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex max-w-[860px] flex-col gap-[var(--space-6)] px-[var(--space-8)] py-[var(--space-8)]">
      <header>
        <h1 className="text-[length:var(--text-3xl)] font-extrabold tracking-[var(--tracking-snug)] text-[var(--text-primary)]">
          Engines &amp; models
        </h1>
        <p className="mt-1 text-[length:var(--text-md)] text-[var(--text-secondary)]">{summary}</p>
      </header>

      {problem && (
        <p
          role="alert"
          className="rounded-[var(--radius-lg)] border border-[var(--warning)] bg-[var(--surface-glass)] px-[var(--space-4)] py-[var(--space-3)] text-[length:var(--text-sm)] text-[var(--text-primary)]"
        >
          {problem}
        </p>
      )}

      {data === null ? (
        <p className="text-[length:var(--text-sm)] text-[var(--text-secondary)]">Loading…</p>
      ) : (
        <>
          <WhereEverythingRuns
            data={data}
            busy={busy}
            onApply={(preset) => void applyPreset(preset)}
          />

          <div className="flex flex-col gap-[var(--space-4)]">
            {data.catalogue.map((definition) => {
              const resolved = chosen.get(definition.engine);
              if (!resolved) return null;

              return (
                <EngineCard
                  key={definition.engine}
                  definition={definition}
                  resolved={resolved}
                  workers={data.workers}
                  open={open === definition.engine}
                  busy={busy}
                  onToggle={() =>
                    setOpen((current) => (current === definition.engine ? null : definition.engine))
                  }
                  onSave={(body) => void save(definition.engine, body)}
                  onSaveStep={(step, parameters) =>
                    void saveStep(definition.engine, step, parameters)
                  }
                  onResetStep={(step) => void resetStep(definition.engine, step)}
                />
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

function WhereEverythingRuns({
  data,
  busy,
  onApply,
}: {
  data: EngineSettingsPayload;
  busy: boolean;
  onApply: (preset: string) => void;
}) {
  // Only the engines that have somewhere to run. Counting the four that run nowhere as
  // "local" would read as seven engines working, which is the small kind of lie this
  // screen exists not to tell — so they are counted apart and named.
  const runs = (engine: string) =>
    data.catalogue.find((d) => d.engine === engine)?.placements.some((p) => p.available) ?? false;

  const counts = { worker: 0, server: 0, cloud: 0 };
  let waiting = 0;

  for (const engine of data.engines) {
    if (runs(engine.engine)) counts[engine.placement as keyof typeof counts] += 1;
    else waiting += 1;
  }

  const active = (preset: (typeof data.presets)[number]) =>
    Object.entries(preset.placements).every(
      ([engine, placement]) => data.engines.find((e) => e.engine === engine)?.placement === placement,
    );

  // A preset is only offerable when every placement it names exists. One that put the
  // Transcriber in a cloud nothing calls yet would either be refused by the Server or,
  // worse, applied in part — so it is drawn with the milestone that unlocks it instead.
  const blocker = (preset: (typeof data.presets)[number]) => {
    for (const [engine, placement] of Object.entries(preset.placements)) {
      const option = data.catalogue
        .find((d) => d.engine === engine)
        ?.placements.find((p) => p.placement === placement);

      if (option && !option.available) return option.milestone;
    }
    return null;
  };

  return (
    <section className="rounded-[var(--radius-xl)] border border-[var(--border-subtle)] bg-[var(--surface-glass)] p-[var(--space-4)] backdrop-blur-[var(--blur-sm)]">
      <div className="flex items-center justify-between gap-[var(--space-4)]">
        <h2 className="text-[length:var(--text-lg)] font-bold tracking-[var(--tracking-snug)] text-[var(--text-primary)]">
          Where everything runs
        </h2>
        <span className="font-[family-name:var(--font-mono)] text-[length:var(--text-xs)] text-[var(--text-muted)]">
          {counts.worker} local · {counts.server} on the server · {counts.cloud} cloud
          {waiting > 0 && ` · ${waiting} not yet built`}
        </span>
      </div>

      <div className="mt-[var(--space-3)] grid grid-cols-2 gap-[var(--space-2)]">
        {data.presets.map((preset) => {
          const on = active(preset);
          const milestone = blocker(preset);

          return (
            <button
              key={preset.id}
              type="button"
              disabled={busy || milestone !== null}
              title={milestone === null ? preset.note : undefined}
              onClick={() => onApply(preset.id)}
              className={cn(
                "rounded-[var(--radius-lg)] border px-[var(--space-3)] py-[var(--space-2)] text-left",
                on
                  ? "border-[var(--border-strong)] bg-[var(--sidebar-accent)]"
                  : "border-[var(--border-subtle)] bg-[var(--surface-glass)]",
                milestone !== null && "opacity-60",
              )}
            >
              <span
                className={cn(
                  "block text-[length:var(--text-sm)]",
                  on ? "font-semibold text-[var(--text-primary)]" : "text-[var(--text-secondary)]",
                )}
              >
                {preset.label}
              </span>
              <span className="mt-[3px] block text-[length:var(--text-2xs)] text-[var(--text-muted)]">
                {milestone === null
                  ? preset.note
                  : `Waiting on ${milestone} — it puts an engine somewhere nothing runs yet.`}
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );
}

function EngineCard({
  definition,
  resolved,
  workers,
  open,
  busy,
  onToggle,
  onSave,
  onSaveStep,
  onResetStep,
}: {
  definition: EngineDefinition;
  resolved: ResolvedEngine;
  workers: WorkerInventory[];
  open: boolean;
  busy: boolean;
  onToggle: () => void;
  onSave: (body: { placement: string; model?: string | null; parameters?: Record<string, unknown> }) => void;
  onSaveStep: (step: string, parameters: Record<string, unknown>) => void;
  onResetStep: (step: string) => void;
}) {
  const Icon = ICONS[definition.engine] ?? Boxes;
  const placement =
    definition.placements.find((p) => p.placement === resolved.placement) ?? definition.placements[0]!;
  const model = placement.models.find((m) => m.id === resolved.model) ?? placement.models[0]!;
  const colour = PLACEMENT_COLOUR[placement.placement] ?? "var(--vl-life)";
  const runnable = definition.placements.some((p) => p.available);

  return (
    <section
      className={cn(
        "overflow-hidden rounded-[var(--radius-xl)] border bg-[var(--surface-glass)] backdrop-blur-[var(--blur-sm)]",
        open ? "border-[var(--border-strong)]" : "border-[var(--border-subtle)]",
      )}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex w-full items-center gap-[var(--space-3)] p-[var(--space-4)] text-left"
      >
        <Icon size={16} strokeWidth={2} className="shrink-0 text-[var(--text-muted)]" />

        <span className="min-w-[130px] font-bold text-[var(--text-primary)]">{definition.engine}</span>

        <span className="min-w-0 flex-1 text-[length:var(--text-xs)] text-[var(--text-muted)]">
          {definition.what}
        </span>

        {runnable ? (
          <>
            <span
              className="shrink-0 rounded-[var(--radius-full)] px-[9px] py-[2px] text-[length:var(--text-2xs)] font-semibold"
              style={{
                color: colour,
                background: `color-mix(in oklch, ${colour} 14%, transparent)`,
              }}
            >
              {placement.label}
            </span>

            <span className="max-w-[200px] shrink-0 truncate font-[family-name:var(--font-mono)] text-[length:var(--text-xs)] text-[var(--text-secondary)]">
              {model.label}
            </span>
          </>
        ) : (
          // Nothing runs this yet, so it is in no placement and using no model. A pill
          // saying "Local" beside a model name would read as a choice somebody made.
          <span className="shrink-0 rounded-[var(--radius-full)] bg-[var(--surface-2)] px-[9px] py-[2px] text-[length:var(--text-2xs)] font-semibold text-[var(--text-muted)]">
            Arrives in {definition.placements[0]?.milestone}
          </span>
        )}

        {open ? (
          <ChevronDown size={14} strokeWidth={2} className="shrink-0 text-[var(--text-muted)]" />
        ) : (
          <ChevronRight size={14} strokeWidth={2} className="shrink-0 text-[var(--text-muted)]" />
        )}
      </button>

      {open && (
        <div className="flex flex-col gap-[var(--space-4)] border-t border-[var(--border-subtle)] p-[var(--space-4)]">
          <div className="flex flex-wrap items-center gap-[8px]">
            <span className="text-[length:var(--text-2xs)] uppercase tracking-[var(--tracking-wider)] text-[var(--text-muted)]">
              Used by
            </span>
            {definition.steps.map((step) => (
              <span
                key={step}
                className="rounded-[var(--radius-full)] bg-[var(--secondary)] px-[9px] py-[2px] font-[family-name:var(--font-mono)] text-[length:var(--text-2xs)] text-[var(--text-secondary)]"
              >
                {step}
              </span>
            ))}
          </div>

          {!runnable && (
            <p className="text-[length:var(--text-sm)] text-[var(--text-secondary)]">
              Nothing runs the {definition.engine} yet — it arrives in{" "}
              {definition.placements[0]?.milestone}. What is below is what it will offer.
            </p>
          )}

          <div className="flex flex-wrap items-center gap-[var(--space-4)]">
            <div className="flex gap-[6px]">
              {definition.placements.map((option) => {
                const on = option.placement === placement.placement;
                const tint = PLACEMENT_COLOUR[option.placement] ?? "var(--vl-life)";

                return (
                  <button
                    key={option.placement}
                    type="button"
                    disabled={busy || !option.available}
                    title={
                      option.available
                        ? option.hint
                        : `Nothing runs this yet — it arrives in ${option.milestone}.`
                    }
                    onClick={() => onSave({ placement: option.placement })}
                    className={cn(
                      "rounded-[var(--radius-full)] border px-[var(--space-3)] py-[5px] text-[length:var(--text-xs)]",
                      on ? "font-semibold" : "font-normal",
                      !option.available && "opacity-50",
                    )}
                    style={
                      on
                        ? {
                            color: tint,
                            borderColor: `color-mix(in oklch, ${tint} 45%, transparent)`,
                            background: `color-mix(in oklch, ${tint} 18%, transparent)`,
                          }
                        : {
                            color: "var(--text-muted)",
                            borderColor: "var(--border-subtle)",
                            background: "transparent",
                          }
                    }
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>

            <span className="min-w-[200px] flex-1 text-[length:var(--text-xs)] text-[var(--text-muted)]">
              {placement.hint}
            </span>
          </div>

          <div className="flex flex-wrap items-center gap-[var(--space-4)] rounded-[var(--radius-lg)] bg-[var(--surface-2)] p-[12px]">
            <div className="flex min-w-[220px] flex-1 flex-col gap-[4px]">
              <label
                htmlFor={`model-${definition.engine}`}
                className="text-[length:var(--text-2xs)] uppercase tracking-[var(--tracking-wider)] text-[var(--text-muted)]"
              >
                Model
              </label>
              <select
                id={`model-${definition.engine}`}
                value={model.id}
                disabled={busy || !placement.available || placement.models.length < 2}
                onChange={(event) =>
                  onSave({ placement: placement.placement, model: event.target.value })
                }
                className="rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-1)] px-[var(--space-2)] py-[6px] text-[length:var(--text-sm)] text-[var(--text-primary)] disabled:opacity-60"
              >
                {placement.models.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>
              {model.note && (
                <span className="text-[length:var(--text-xs)] text-[var(--text-muted)]">
                  {model.note}
                </span>
              )}
            </div>

            <ModelStatus placement={placement} model={model.id} size={model.size} workers={workers} />
          </div>

          {placement.parameters.length === 0 ? (
            <p className="text-[length:var(--text-xs)] text-[var(--text-muted)]">
              Nothing to tune here — this placement takes no settings.
            </p>
          ) : (
            <div className="grid grid-cols-2 gap-x-[var(--space-6)] gap-y-[var(--space-4)]">
              {placement.parameters.map((parameter) => (
                <Parameter
                  key={parameter.key}
                  scope={definition.engine}
                  parameter={parameter}
                  value={resolved.parameters[parameter.key]}
                  room={room(parameter, resolved.parameters[parameter.key], placement, model.id, workers)}
                  disabled={busy || !placement.available}
                  onChange={(value) =>
                    onSave({
                      placement: placement.placement,
                      model: model.id,
                      parameters: { ...resolved.parameters, [parameter.key]: value },
                    })
                  }
                />
              ))}
            </div>
          )}

          {definition.scopesBySteps && resolved.steps.length > 0 && (
            <PerStep
              engine={definition.engine}
              placement={placement}
              model={model.id}
              workers={workers}
              steps={resolved.steps}
              busy={busy}
              onSave={onSaveStep}
              onReset={onResetStep}
            />
          )}
        </div>
      )}
    </section>
  );
}

/**
 * ARCH-12 — each step's own thinking, sampling and answer ceiling, under the engine's.
 *
 * <b>One row per step, one expanded at a time.</b> The milestone's constraint is that
 * five steps have to stay readable under one accordion, and fifteen controls unfolded
 * inside a card that is already an accordion is not readable — so at rest each step is a
 * line saying what it is set to, and opening one shows its controls. That is the same
 * shape as the engine card containing it, one level down, rather than a new idiom.
 *
 * <b>Every step has values.</b> There is no "not configured" state to draw, because the
 * catalogue ships a profile per step: a row nobody has touched is still a correction
 * pass that does not deliberate. What the pill marks is the narrower thing — a value
 * this organization chose for this step, which is what <i>Reset</i> removes.
 */
function PerStep({
  engine,
  placement,
  model,
  workers,
  steps,
  busy,
  onSave,
  onReset,
}: {
  engine: string;
  placement: EnginePlacement;
  model: string;
  workers: WorkerInventory[];
  steps: ResolvedStep[];
  busy: boolean;
  onSave: (step: string, parameters: Record<string, unknown>) => void;
  onReset: (step: string) => void;
}) {
  const [open, setOpen] = useState<string | null>(null);

  // What can be set for one step, on the placement it is actually running on. The local
  // Reasoner has a Think switch and a Sampling picker; the cloud one has neither, because
  // that client constrains nothing and samples nothing — so the rows shrink rather than
  // drawing controls that would change nothing.
  const scoped = placement.parameters.filter((parameter) => parameter.perStep);

  // And of those, only what this step reads: the name-correction threshold is
  // `correct_transcript`'s, and drawing it on every other row is a control that does nothing.
  const readBy = (step: string) =>
    scoped.filter((parameter) => parameter.steps === null || parameter.steps.includes(step));

  if (scoped.length === 0) return null;

  return (
    <div className="flex flex-col gap-[var(--space-2)]">
      <div className="flex items-baseline justify-between gap-[var(--space-3)]">
        <span className="text-[length:var(--text-2xs)] uppercase tracking-[var(--tracking-wider)] text-[var(--text-muted)]">
          Per step
        </span>
        <span className="text-[length:var(--text-2xs)] text-[var(--text-muted)]">
          Each falls back to the values above.
        </span>
      </div>

      <div className="overflow-hidden rounded-[var(--radius-lg)] border border-[var(--border-subtle)] bg-[var(--surface-2)]">
        {steps.map((step, index) => {
          const expanded = open === step.step;
          const short = readBy(step.step).some(
            (parameter) => room(parameter, step.parameters[parameter.key], placement, model, workers)?.short,
          );

          return (
            <div
              key={step.step}
              className={cn(index > 0 && "border-t border-[var(--border-subtle)]")}
            >
              <button
                type="button"
                aria-expanded={expanded}
                onClick={() => setOpen((current) => (current === step.step ? null : step.step))}
                className="flex w-full items-center gap-[var(--space-3)] px-[var(--space-3)] py-[10px] text-left hover:bg-[var(--surface-1)]"
              >
                {expanded ? (
                  <ChevronDown size={13} strokeWidth={2} className="shrink-0 text-[var(--text-muted)]" />
                ) : (
                  <ChevronRight size={13} strokeWidth={2} className="shrink-0 text-[var(--text-muted)]" />
                )}

                <span className="min-w-[150px] shrink-0 font-[family-name:var(--font-mono)] text-[length:var(--text-2xs)] text-[var(--text-secondary)]">
                  {step.step}
                </span>

                {/* What it is set to, as words rather than as controls. This is the line
                    somebody scans to answer "does correction think?" without opening
                    anything, which is the whole reason the rows collapse. */}
                <span className="flex-1 truncate text-[length:var(--text-xs)] text-[var(--text-muted)]">
                  {readBy(step.step)
                    .map((parameter) => `${parameter.label.toLowerCase()} ${describe(parameter, step.parameters[parameter.key])}`)
                    .join(" · ")}
                </span>

                {/* Said on the collapsed line too, because a ceiling the worker cannot hold is
                    a step that fails on its next long transcript, not a detail to find later. */}
                {short && (
                  <span className="flex shrink-0 items-center gap-[4px] rounded-[var(--radius-full)] bg-[color-mix(in_oklch,var(--warning)_14%,transparent)] px-[9px] py-[2px] text-[length:var(--text-2xs)] font-semibold text-[var(--warning)]">
                    <TriangleAlert size={11} strokeWidth={2} />
                    Over the worker&apos;s room
                  </span>
                )}

                {step.explicit && (
                  <span className="shrink-0 rounded-[var(--radius-full)] bg-[var(--secondary)] px-[9px] py-[2px] text-[length:var(--text-2xs)] font-semibold text-[var(--text-secondary)]">
                    Changed
                  </span>
                )}
              </button>

              {expanded && (
                <div className="flex flex-col gap-[var(--space-3)] border-t border-[var(--border-subtle)] px-[var(--space-3)] py-[var(--space-3)]">
                  {step.why && (
                    <p className="text-[length:var(--text-xs)] text-[var(--text-secondary)]">{step.why}</p>
                  )}

                  <div className="grid grid-cols-2 gap-x-[var(--space-6)] gap-y-[var(--space-4)]">
                    {readBy(step.step).map((parameter) => (
                      <Parameter
                        key={parameter.key}
                        scope={`${engine}-${step.step}`}
                        parameter={parameter}
                        value={step.parameters[parameter.key]}
                        room={room(parameter, step.parameters[parameter.key], placement, model, workers)}
                        disabled={busy || !placement.available}
                        onChange={(value) =>
                          onSave(step.step, { ...step.parameters, [parameter.key]: value })
                        }
                      />
                    ))}
                  </div>

                  {step.explicit && (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => onReset(step.step)}
                      className="flex items-center gap-[6px] self-start text-[length:var(--text-xs)] text-[var(--text-muted)] hover:text-[var(--text-primary)] disabled:opacity-60"
                    >
                      <RotateCcw size={12} strokeWidth={2} />
                      Back to the engine&apos;s values
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** One resolved value as a word, for the collapsed summary line. */
function describe(parameter: EngineParameter, value: string | number | boolean | undefined): string {
  const current = value ?? parameter.default ?? "—";

  if (current === true || current === "true") return "on";
  if (current === false || current === "false") return "off";

  return optionLabel(parameter, String(current));
}

/** What the room on the workers says about one answer ceiling. */
interface Room {
  /** True when the ceiling is at or over what some worker could ever give it. */
  short: boolean;
  text: string;
}

/**
 * Whether a local answer ceiling fits the context the worker's model was actually loaded
 * with. The prompt and the answer share that context, so a ceiling at or over it can never
 * be reached: a long transcript is refused, and the model is never given the room it was
 * told it has.
 *
 * Measured, not known: the sidecar sizes the context from what is left on the card after
 * the weights, so it moves with each machine's VRAM budget (14336 MiB leaves about 5.6k
 * tokens). Until a worker has answered a local step, this says so rather than guessing.
 */
function room(
  parameter: EngineParameter,
  value: string | number | boolean | undefined,
  placement: EnginePlacement,
  model: string,
  workers: WorkerInventory[],
): Room | null {
  if (parameter.key !== "maxOutputTokens" || placement.placement !== "worker") return null;

  const ceiling = Number(value ?? parameter.default);
  if (!Number.isFinite(ceiling)) return null;

  const measured = workers.filter((worker) => worker.reasonerRoom?.model === model);

  if (measured.length === 0) {
    return {
      short: false,
      text: "How much room the local model has is known once a worker has answered a local step.",
    };
  }

  const short = measured.filter((worker) => worker.reasonerRoom!.contextTokens <= ceiling);
  if (short.length === 0) return null;

  const smallest = Math.min(...short.map((worker) => worker.reasonerRoom!.contextTokens));
  const names = short.map((worker) => worker.name ?? "an unnamed worker").join(", ");

  return {
    short: true,
    text:
      `The local model has room for ${tokens(smallest)} tokens on ${names}, prompt and answer together, ` +
      "so this ceiling can never be reached there. Lower it, or give the worker a larger VRAM budget.",
  };
}

/** A measured token count, in the same k as the options: 5700 is "5.6k". */
function tokens(count: number): string {
  return count < 1024 ? String(count) : `${(count / 1024).toFixed(1).replace(/\.0$/, "")}k`;
}

/**
 * How an option reads on screen. A token count is stored as the number the model is sent
 * and shown as people say it: 16384 is "16k". Anything else reads as stored.
 */
function optionLabel(parameter: EngineParameter, option: string): string {
  if (parameter.key !== "maxOutputTokens") return option;

  const tokens = Number(option);
  return tokens >= 1024 && tokens % 1024 === 0 ? `${tokens / 1024}k` : option;
}

/**
 * Whether the chosen model is anywhere it could actually run.
 *
 * This is the milestone's "a config referencing a model that is not downloaded
 * surfaces as a setup issue rather than a step failure", and it is the reason the claim
 * poll carries an inventory at all. There is no percentage: the sidecar fetches a model
 * as one call and reports what it found, so a progress bar would be a number nothing
 * measured — the same reason Transcript Review draws no waveform (DECISIONS.md 2026-08-28).
 */
function ModelStatus({
  placement,
  model,
  size,
  workers,
}: {
  placement: EnginePlacement;
  model: string;
  size: string | null;
  workers: WorkerInventory[];
}) {
  if (placement.placement === "server") {
    return <Status colour="var(--success)" text="Runs on the Server VM" where={placement.where} />;
  }

  if (placement.placement === "cloud") {
    return (
      <Status colour="var(--text-muted)" text="Needs a key in Cloud providers" where={placement.where} />
    );
  }

  const state = modelState(model, workers);
  const suffix = size ? ` · ${size}` : "";

  const answers: Record<ModelState, { colour: string; text: string }> = {
    present: { colour: "var(--success)", text: `On the worker${suffix}` },
    fetching: { colour: "var(--vl-bridge)", text: "Fetching weights…" },
    failed: { colour: "var(--warning)", text: `The download failed${suffix}` },
    missing: { colour: "var(--warning)", text: `Not on this worker yet${suffix}` },
    "no-worker": { colour: "var(--warning)", text: "No worker has said what it has" },
  };

  const answer = answers[state];

  return (
    <div className="flex flex-col items-end gap-[4px]">
      <span
        className="text-[length:var(--text-xs)] font-semibold"
        style={{ color: answer.colour }}
      >
        {answer.text}
      </span>
      <span className="font-[family-name:var(--font-mono)] text-[length:var(--text-2xs)] text-[var(--text-muted)]">
        {placement.where}
      </span>
      {state === "missing" && (
        <span className="max-w-[220px] text-right text-[length:var(--text-2xs)] text-[var(--text-muted)]">
          The worker fetches it the next time it asks for work, or when a step needs it.
        </span>
      )}
    </div>
  );
}

function Status({ colour, text, where }: { colour: string; text: string; where: string }) {
  return (
    <div className="flex flex-col items-end gap-[4px]">
      <span className="text-[length:var(--text-xs)] font-semibold" style={{ color: colour }}>
        {text}
      </span>
      <span className="font-[family-name:var(--font-mono)] text-[length:var(--text-2xs)] text-[var(--text-muted)]">
        {where}
      </span>
    </div>
  );
}

/**
 * The best answer any worker gives, because one machine having the weights is enough
 * for the pipeline to run. "No worker has said" is kept apart from "not downloaded":
 * one is a machine that is off, the other is a machine that is on and does not have it.
 */
function modelState(model: string, workers: WorkerInventory[]): ModelState {
  if (workers.length === 0) return "no-worker";

  const reported = workers.map((worker) => worker.models[model]).filter(Boolean);
  if (reported.length === 0) return "no-worker";

  if (reported.includes("present")) return "present";
  if (reported.includes("fetching")) return "fetching";
  if (reported.includes("failed")) return "failed";
  return "missing";
}

function Parameter({
  scope,
  parameter,
  value,
  room,
  disabled,
  onChange,
}: {
  /** What this control belongs to — an engine, or an engine and a step (ARCH-12). One
   * parameter can now be drawn twice on one screen, and two labels pointing at the same
   * id is a label that focuses the wrong control. */
  scope: string;
  parameter: EngineDefinition["placements"][number]["parameters"][number];
  value: string | number | boolean | undefined;
  /** For a local answer ceiling: whether it fits the room the worker measured. */
  room?: Room | null;
  disabled: boolean;
  onChange: (value: string | number | boolean) => void;
}) {
  const id = `parameter-${scope}-${parameter.key}`;

  // An absent value is the catalogue's default, which for a select is usually "auto" —
  // the Server drops that word on the way in rather than storing it, so nothing
  // downstream has to know it (SPCH-1's language does the same).
  const current = value ?? parameter.default ?? "";

  return (
    <div className="flex flex-col gap-[6px]">
      <div className="flex items-center justify-between gap-[10px]">
        <label htmlFor={id} className="text-[length:var(--text-sm)] font-medium text-[var(--text-primary)]">
          {parameter.label}
        </label>

        {parameter.kind === "switch" && (
          <input
            id={id}
            type="checkbox"
            checked={current === true || current === "true"}
            disabled={disabled}
            onChange={(event) => onChange(event.target.checked)}
            className="h-[18px] w-[34px] accent-[var(--primary)]"
          />
        )}
      </div>

      {parameter.kind === "select" && (
        <select
          id={id}
          value={String(current)}
          disabled={disabled}
          onChange={(event) => onChange(event.target.value)}
          className="rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-1)] px-[var(--space-2)] py-[6px] text-[length:var(--text-sm)] text-[var(--text-primary)] disabled:opacity-60"
        >
          {(parameter.options ?? []).map((option) => (
            <option key={option} value={option}>
              {optionLabel(parameter, option)}
            </option>
          ))}
        </select>
      )}

      {parameter.kind === "number" && (
        <input
          id={id}
          type="number"
          value={String(current)}
          min={parameter.minimum ?? undefined}
          max={parameter.maximum ?? undefined}
          disabled={disabled}
          onChange={(event) => onChange(Number(event.target.value))}
          className="rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-1)] px-[var(--space-2)] py-[6px] text-[length:var(--text-sm)] text-[var(--text-primary)] disabled:opacity-60"
        />
      )}

      {parameter.hint && (
        <span className="text-[length:var(--text-2xs)] text-[var(--text-muted)]">{parameter.hint}</span>
      )}

      {room &&
        (room.short ? (
          <span className="flex items-start gap-[6px] text-[length:var(--text-2xs)] text-[var(--warning)]">
            <TriangleAlert size={12} strokeWidth={2} className="mt-[1px] shrink-0" />
            {room.text}
          </span>
        ) : (
          <span className="text-[length:var(--text-2xs)] text-[var(--text-muted)]">{room.text}</span>
        ))}
    </div>
  );
}
